import { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, PageHeader } from '@billda/design-system';
import { MailOutlineIcon, PersonAddIcon } from '@billda/design-system';
import { useFeature, Features } from '@billda/features-ui';
import { useNavigate } from 'react-router-dom';
import { UserStatus } from '../api/userManagementApi';
import { PageHeaderAction } from '../../../shared/components/layout/PageHeaderAction';
import { PageContentArea } from '../../../shared/components/layout/PageContentArea';
import { PageToolbar } from '../../../shared/components/layout/PageToolbar';
import { ChipTabs } from '../../../shared/components/ui/ChipTabs';
import { useUsers } from '../hooks/useUsers';
import { useUserManagementUIStore } from '../stores/userManagementUIStore';
import { useUserPanelState } from '../hooks/useUserPanelState';
import { UsersListView } from '../components/organisms/UsersListView';
import InviteUserPanel from '../components/InviteUserPanel';
import PendingInvitesPanel from '../components/PendingInvitesPanel';
import { usePendingInvites } from '../hooks/usePendingInvites';
import { ResizableHandle, SidePanel } from '../../../shared/components/panel';
import { HelpPanel } from '../../../shared/components/help/HelpPanel';
import { USERS_HELP } from '../../../shared/constants/helpContent';
import { useEscapeKey } from '../../../shared/hooks/useEscapeKey';
import { routeBuilders } from '../../../routes';

const OFFBOARDED_TAB_VALUE = '__offboarded__';

const UserManagementPage = () => {
  const navigate = useNavigate();

  // Use React Query for users list
  const { data: users = [], isLoading: loadingUsers } = useUsers();

  // Get pending invites count for badge
  const { data: pendingData } = usePendingInvites();
  const pendingCount = pendingData?.totalCount ?? 0;

  // Offboarding feature flag
  const isOffboardingEnabled = useFeature(Features.Offboarding.IsEnabled);
  const { showOffboarded, setShowOffboarded } = useUserManagementUIStore();

  // Role filter state
  const [roleFilter, setRoleFilter] = useState('all');

  // UI state from store
  const { searchText, setSearchText } = useUserManagementUIStore();

  // Panel state from URL
  const {
    isOpen: isPanelOpen,
    type: contentType,
    openPanel,
    closePanel,
  } = useUserPanelState();

  // ESC key to close panel
  useEscapeKey(closePanel, isPanelOpen);

  const loading = loadingUsers;

  // Derive unique roles from users for ChipTabs
  const roleTabs = useMemo(() => {
    const roles = new Set<string>();
    users.forEach(user => user.roles?.forEach(role => roles.add(role)));
    const sorted = Array.from(roles).sort();

    const tabs = [{ label: 'All', value: 'all' }];

    if (isOffboardingEnabled && showOffboarded) {
      tabs.push({ label: 'Offboarded', value: OFFBOARDED_TAB_VALUE });
    }

    if (isOffboardingEnabled && sorted.includes('Offboarding')) {
      tabs.push({ label: 'Offboarding', value: 'Offboarding' });
      const remaining = sorted.filter(r => r !== 'Offboarding');
      tabs.push(...remaining.map(role => ({ label: role, value: role })));
    } else {
      tabs.push(...sorted.map(role => ({ label: role, value: role })));
    }

    return tabs;
  }, [users, isOffboardingEnabled, showOffboarded]);

  // Filter users based on search text and role
  const filteredUsers = useMemo(() => {
    let filtered = users;

    // Hide offboarded users by default when feature flag is on
    if (isOffboardingEnabled && !showOffboarded) {
      filtered = filtered.filter(user => user.status !== UserStatus.Inactive);
    }

    // Role filter
    if (roleFilter === OFFBOARDED_TAB_VALUE) {
      filtered = filtered.filter(user => user.status === UserStatus.Inactive);
    } else if (roleFilter !== 'all') {
      filtered = filtered.filter(user =>
        user.roles?.some(role => role === roleFilter)
      );
    }

    // Search filter
    if (searchText.trim()) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        user =>
          user.fullName.toLowerCase().includes(searchLower) ||
          (user.email && user.email.toLowerCase().includes(searchLower)) ||
          (user.roles &&
            user.roles.some(role =>
              role.toLowerCase().includes(searchLower)
            )) ||
          ((user as any).username &&
            (user as any).username.toLowerCase().includes(searchLower))
      );
    }

    return filtered;
  }, [users, searchText, roleFilter, isOffboardingEnabled, showOffboarded]);

  // Reset role filter when offboarded tab is hidden
  useEffect(() => {
    if (!showOffboarded && roleFilter === OFFBOARDED_TAB_VALUE) {
      setRoleFilter('all');
    }
  }, [showOffboarded, roleFilter]);

  const handleUserSelect = (userId: string) => {
    navigate(routeBuilders.users.detail(userId));
  };

  const getPanelTitle = () => {
    if (contentType === 'invite') {
      return 'Invite User';
    }
    if (contentType === 'pending-invites') {
      return 'Pending Invites';
    }
    if (contentType === 'help') {
      return 'Help';
    }
    return 'Details';
  };

  return (
    <PageContentArea
      isPanelOpen={isPanelOpen}
      panel={
        isPanelOpen ? (
          <>
            <ResizableHandle />
            <SidePanel
              open={isPanelOpen}
              onClose={closePanel}
              title={getPanelTitle()}
              testId={
                contentType === 'invite'
                  ? 'invite-user-drawer'
                  : contentType === 'pending-invites'
                    ? 'pending-invites-drawer'
                    : contentType === 'help'
                      ? 'users-help-drawer'
                      : undefined
              }
            >
              {contentType === 'invite' && (
                <InviteUserPanel
                  onClose={closePanel}
                  onInviteSent={() => openPanel('pending-invites', 'list')}
                />
              )}
              {contentType === 'pending-invites' && <PendingInvitesPanel />}
              {contentType === 'help' && <HelpPanel helpContent={USERS_HELP} />}
            </SidePanel>
          </>
        ) : undefined
      }
    >
      <PageHeader
        title="Users"
        helpAction={{
          onClick: () => openPanel('help', 'users'),
          testId: 'users-help-button',
        }}
        actions={
          <>
            <PageHeaderAction
              label={`Invited${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
              onClick={() => openPanel('pending-invites', 'list')}
              icon={<MailOutlineIcon className="size-3.5" />}
              disabled={loading}
              data-testid="pending-invites-button"
            />
            <PageHeaderAction
              label="Add User"
              onClick={() => openPanel('invite', 'new')}
              icon={<PersonAddIcon className="size-3.5" />}
              disabled={loading}
              data-testid="invite-user-button"
            />
          </>
        }
        data-testid="users-header"
      />

      <PageToolbar
        tabs={
          <ChipTabs
            tabs={roleTabs}
            value={roleFilter}
            onChange={setRoleFilter}
            data-testid="users-role-tabs"
          />
        }
        searchValue={searchText}
        onSearchChange={setSearchText}
        filterContent={
          isOffboardingEnabled ? (
            <label
              className="flex items-center gap-2 type-body-md"
              data-testid="show-offboarded-toggle"
            >
              <Checkbox
                checked={showOffboarded}
                onCheckedChange={checked => setShowOffboarded(checked === true)}
                data-testid="show-offboarded-checkbox"
              />
              Show offboarded users
            </label>
          ) : undefined
        }
      />

      <div className="min-h-0 flex-1 overflow-auto">
        <UsersListView
          users={filteredUsers}
          loading={loading}
          onItemClick={handleUserSelect}
        />
        {!loadingUsers && users.length > 0 && users.length < 10 && (
          <div className="flex items-center justify-center p-8">
            <div className="flex max-w-[400px] flex-col items-center gap-4">
              <PersonAddIcon
                aria-hidden="true"
                className="size-12 text-content-secondary"
              />
              <h6 className="type-h6 text-content-primary">Grow your team</h6>
              <p className="type-body-md text-center text-content-secondary">
                Invite more workers so they can book work on your sites and get
                paid via the mobile app.
              </p>
              <Button
                variant="neutral"
                onClick={() => openPanel('invite', 'new')}
                data-testid="users-cta-add-user"
              >
                <PersonAddIcon aria-hidden="true" className="size-4" />
                Add User
              </Button>
            </div>
          </div>
        )}
      </div>
    </PageContentArea>
  );
};

export default UserManagementPage;
